import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { API_S3_BASE_URL } from '../../../../../apiConstant';
import MediaViewer from '../../../../message/components/MediaViewer';
import InkLayerPDFViewer from '../../../../message/components/InkLayerPDFViewer';
import { Can } from '../../../../../utils/accessControl';
import { useViewS3FileById } from '../../../../../utils/hooks/useViewS3FileById';
import { useUpdateS3JsonEditInfo } from '../../../../../utils/hooks/useUpdateS3JsonEditInfo';

import PRIVILEGES from '../../../../../constants/privileges';
import { useRBACStore } from '../../../../../stores/useRBACStore';
import { useAuthStore } from '../../../../../stores/useAuthStore';
import Button from '../../../../../uikit/button';
import LinkTaskModal from './LinkTaskModal';

function DisplayFiles({
  files = [],
  pID,
  onDelete,
  enableDelete = false,
  enableLinkTask = false,
  viewPrivilege,
  downloadPrivilege,
  deletePrivilege,
  enableApprove = false,
  approvedStorageFileId = null,
  approvePrivilege,
  onApprove,
  onRevoke,
  approvePending = false,
}) {
  const [selectedFile, setSelectedFile] = useState(null); // { url, fileName }
  const [selectedPdfData, setSelectedPdfData] = useState(null); // ArrayBuffer for PDF
  const [selectedPdfAnnotations, setSelectedPdfAnnotations] = useState([]);

  const { mutateAsync: viewFile } = useViewS3FileById();
  const { mutateAsync: updateJsonEditInfo } = useUpdateS3JsonEditInfo();
  const [selectedFileForLinking, setSelectedFileForLinking] = useState(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [currentViewingFile, setCurrentViewingFile] = useState(null);

  const authUser = useAuthStore((s) => s.user);
  const inklayerUser = authUser
    ? {
        id: authUser.id || authUser.userId || authUser.uid || 'unknown',
        name: authUser.name || authUser.username || authUser.displayName || 'Unknown',
      }
    : undefined;

  const deleteFileMutation = useMutation({
    mutationFn: async (id) => {
      return axios.delete(`${API_S3_BASE_URL}/api/s3/delete-by-id`, {
        params: { id },
      });
    },
    onSuccess: (_, id) => {
      onDelete?.(id);
    },
  });

  const handleView = async (file) => {
    try {
      const data = await viewFile(file.id);
      const url = data?.url;

      setCurrentViewingFile(file);

      if (url) {
        const jsonEditInfo = data?.jsonEditInfo;
        let parsedAnnotations = [];

        if (jsonEditInfo) {
          try {
            const normalized = typeof jsonEditInfo === 'string' ? JSON.parse(jsonEditInfo) : jsonEditInfo;
            if (Array.isArray(normalized)) {
              parsedAnnotations = normalized;
            } else if (normalized && typeof normalized === 'object') {
              parsedAnnotations = Array.isArray(normalized.annotations)
                ? normalized.annotations
                : Array.isArray(normalized.data)
                ? normalized.data
                : [];
            }
          } catch (parseError) {
            console.warn('Failed to parse jsonEditInfo:', parseError, jsonEditInfo);
            parsedAnnotations = [];
          }
        }

        setSelectedPdfAnnotations(parsedAnnotations);

        if (file?.fileName?.toLowerCase().endsWith('.pdf')) {
          // For PDF, revoke any previous object URL and fetch as ArrayBuffer to pass directly to PdfAnnotator
          if (selectedFile?.url) {
            window.URL.revokeObjectURL(selectedFile.url);
            setSelectedFile(null);
          }

          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF. HTTP status ${response.status}`);
          }
          const arrayBuffer = await response.arrayBuffer();
          setSelectedPdfData(arrayBuffer);
        } else {
          // For images/videos, create blob URL
          if (selectedFile?.url) {
            window.URL.revokeObjectURL(selectedFile.url);
          }

          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch file. HTTP status ${response.status}`);
          }
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          setSelectedFile({ url: blobUrl, fileName: file.fileName });
          setSelectedPdfData(null);
        }
      } else {
        console.error('No URL returned from view API');
      }
    } catch (error) {
      console.error('Error viewing file:', error);
    }
  };

  const downloadFile = async (url, fileName) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download file. HTTP status ${response.status}`);
      }
      const blob = await response.blob();

      const name = fileName || decodeURI(url.split('/').pop());

      const link = document.createElement('a');
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;
      link.download = name;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDownload = async (file) => {
    try {
      const data = await viewFile(file.id);
      const url = data?.url;

      if (url) {
        await downloadFile(url, file?.fileName);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleCloseFilePreview = () => {
    if (selectedFile?.url) window.URL.revokeObjectURL(selectedFile.url);
    setSelectedFile(null);
  };

  const handleClosePdfPreview = () => {
    setSelectedPdfData(null);
    setSelectedPdfAnnotations([]);
    setCurrentViewingFile(null);
  };

  const handleSavePdfAnnotations = async (annotations) => {
    if (!currentViewingFile?.id) {
      window.triggerAlert?.({ title: 'Unable to save annotations: file not selected', type: 'error' });
      return;
    }

    try {
      const payload = {
        id: currentViewingFile.id,
        jsonEditInfo: JSON.stringify(annotations || []),
      };

      await updateJsonEditInfo(payload);
      setSelectedPdfAnnotations(annotations || []);
      window.triggerAlert?.({ title: 'File annotations saved successfully', type: 'success' });
      console.log('Saved annotations for file:', currentViewingFile.id, annotations);
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to save file annotations';
      window.triggerAlert?.({ title: message, type: 'error' });
      console.error('Failed to save jsonEditInfo:', error);
    }
  };

  if (!files.length) {
    return (
      <div className="flex items-center justify-center h-24 text-gray-400 border border-dashed rounded">
        No Files Found
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {files.map((file) => (
        <div
          key={file.id}
          className="relative flex items-center justify-between p-3 border rounded-md group/card hover:shadow-sm transition-shadow"
        >
          {/* Metadata Info Icon & Tooltip */}
          {file?.metadata && (
            <div className="absolute top-1 left-[-1px] z-100 group/info">
              <button className="text-gray-400 hover:text-blue-500 transition-colors p-1">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </button>
              
              {/* Premium Tooltip */}
              <div className="absolute top-full z-20 left-0 mt-2 w-64 p-3 bg-white border border-gray-200 rounded-xl shadow-xl opacity-0 group-hover/info:opacity-100 transition-all duration-200 pointer-events-none -translate-y-1 group-hover/info:translate-y-0">
                <div className="text-[10px] text-gray-400 uppercase  mb-2 font-bold flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                  File Details
                </div>
                
                {file.metadata.address && (
                  <div className="mb-3">
                    <div className="text-[10px] text-gray-500 font-semibold uppercase mb-0.5">Location</div>
                    <div className="text-[11px] text-gray-800 leading-relaxed font-medium">
                      {file.metadata.address}
                    </div>
                  </div>
                )}
                
                {file.metadata.username && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <div className="text-[10px] text-gray-500 font-semibold uppercase">Uploader</div>
                    <div className="text-[11px] text-blue-600 font-bold">
                      @{file.metadata.username}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="truncate max-w-[200px] font-medium flex items-center gap-2">
            <span className="truncate">{file.fileName}</span>
            {enableApprove &&
              approvedStorageFileId != null &&
              Number(approvedStorageFileId) === Number(file.id) && (
                <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Approved
                </span>
              )}
          </div>

          <div className="flex gap-2 items-center">
            {enableApprove && (
              (() => {
                const isApproved =
                  approvedStorageFileId != null &&
                  Number(approvedStorageFileId) === Number(file.id);
                const approveBtn = isApproved ? (
                  <button
                    type="button"
                    onClick={() => onRevoke?.()}
                    disabled={approvePending}
                    className="px-2.5 py-1 text-xs font-semibold rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    title="Revoke approval"
                  >
                    Revoke
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onApprove?.(file.id)}
                    disabled={approvePending}
                    className="px-2.5 py-1 text-xs font-semibold rounded-full border border-[#FF9800] text-[#FF9800] hover:bg-orange-50 disabled:opacity-50"
                    title="Approve this file"
                  >
                    Approve
                  </button>
                );
                return approvePrivilege ? (
                  <Can privilege={approvePrivilege}>{approveBtn}</Can>
                ) : (
                  approveBtn
                );
              })()
            )}
            {enableLinkTask && (
              <div className="relative inline-block">
                <Button
                  containerClass="mt-1"
                  variant="text"
                  onClick={() => {
                    setSelectedFileForLinking(file);
                    setIsLinkModalOpen(true);
                  }}
                  size="small"
                  startIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-blue-500"
                    >
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  }
                  title="Link Task"
                />
                {file.taskMetadata?.length > 0 && (
                  <div className="absolute -top-0 right-0 translate-x-1/4 -translate-y-1/4 bg-red-500 text-white text-[9px] min-w-[14px] h-[14px] rounded-full flex items-center justify-center font-bold border border-white shadow-sm px-0.5">
                    {file.taskMetadata.length}
                  </div>
                )}
              </div>
            )}

            {(() => {
              const ext = file.fileName?.toLowerCase().split('.').pop();
              const viewableExts = [
                'jpg',
                'jpeg',
                'png',
                'gif',
                'webp',
                'svg',
                'bmp',
                'mp4',
                'webm',
                'ogg',
                'mov',
                'avi',
                'pdf',
              ];
              if (!viewableExts.includes(ext)) return null;

              return viewPrivilege ? (
                <Can privilege={viewPrivilege}>
                  <button
                    onClick={() => handleView(file)}
                    className="p-2 border rounded-full text-blue-500"
                  >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <path
                        d="M12 5C8.24 5 5.44 7.44 3.77 9.44C2.51 10.94 2.51 13.06 3.77 14.56C5.44 16.56 8.24 19 12 19C15.76 19 18.56 16.56 20.23 14.56C21.49 13.06 21.49 10.94 20.23 9.44C18.56 7.44 15.76 5 12 5Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle
                        cx="12"
                        cy="12"
                        r="3"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </Can>
              ) : (
                <button
                  onClick={() => handleView(file)}
                  className="p-2 border rounded-full text-blue-500"
                >
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path
                      d="M12 5C8.24 5 5.44 7.44 3.77 9.44C2.51 10.94 2.51 13.06 3.77 14.56C5.44 16.56 8.24 19 12 19C15.76 19 18.56 16.56 20.23 14.56C21.49 13.06 21.49 10.94 20.23 9.44C18.56 7.44 15.76 5 12 5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              );
            })()}

            {downloadPrivilege ? (
              <Can privilege={downloadPrivilege}>
                <button
                  onClick={() => handleDownload(file)}
                  className="p-2 border rounded-full"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3V15M12 15L7 10M12 15L17 10M5 21H19"
                      stroke="#000"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </Can>
            ) : (
              <button
                onClick={() => handleDownload(file)}
                className="p-2 border rounded-full"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3V15M12 15L7 10M12 15L17 10M5 21H19"
                    stroke="#000"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}

            {enableDelete &&
              (deletePrivilege ? (
                <Can privilege={deletePrivilege}>
                  <button
                    onClick={() => deleteFileMutation.mutate(file.id)}
                    disabled={deleteFileMutation.isPending}
                    className="p-2 border rounded-full text-red-500 disabled:opacity-50"
                  >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <path
                        d="M10 12V17"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M14 12V17"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path d="M4 7H20" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="M6 10V18C6 19.66 7.34 21 9 21H15C16.66 21 18 19.66 18 18V10"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </Can>
              ) : (
                <button
                  onClick={() => deleteFileMutation.mutate(file.id)}
                  disabled={deleteFileMutation.isPending}
                  className="p-2 border rounded-full text-red-500 disabled:opacity-50"
                >
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path d="M10 12V17" stroke="currentColor" strokeWidth="2" />
                    <path d="M14 12V17" stroke="currentColor" strokeWidth="2" />
                    <path d="M4 7H20" stroke="currentColor" strokeWidth="2" />
                    <path
                      d="M6 10V18C6 19.66 7.34 21 9 21H15C16.66 21 18 19.66 18 18V10"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                </button>
              ))}
          </div>
        </div>
      ))}

      {selectedFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={handleCloseFilePreview}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <MediaViewer 
              mediaUrl={selectedFile.url} 
              fileName={selectedFile.fileName} 
            />
          </div>
        </div>
      )}

      {selectedPdfData && (
        <div
          className="fixed inset-0 z-40 flex flex-col h-screen bg-black/50 !mt-0"
          onClick={handleClosePdfPreview}
        >
          <div
            className="flex-1 mb-4 rounded-lg bg-white min-h-0 relative"
            style={{ overflow: 'visible', zIndex: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <InkLayerPDFViewer
              data={selectedPdfData}
              isFullScreen
              title={currentViewingFile?.fileName}
              user={inklayerUser}
              onClose={handleClosePdfPreview}
              onSave={handleSavePdfAnnotations}
              initialAnnotations={selectedPdfAnnotations} />
          </div>
        </div>
      )}

      {isLinkModalOpen && (
        <LinkTaskModal
          isOpen={isLinkModalOpen}
          onClose={() => {
            setIsLinkModalOpen(false);
            setSelectedFileForLinking(null);
          }}
          fileId={`${selectedFileForLinking?.id}`}
          projectId={pID}
          initialTaskIds={selectedFileForLinking?.taskMetadata?.map((t) => t.taskId)}
        />
      )}
    </div>
  );
}

DisplayFiles.propTypes = {
  files: PropTypes.array,
  pID: PropTypes.string,
  onDelete: PropTypes.func,
  enableDelete: PropTypes.bool,
  enableLinkTask: PropTypes.bool,
  viewPrivilege: PropTypes.string,
  downloadPrivilege: PropTypes.string,
  deletePrivilege: PropTypes.string,
  enableApprove: PropTypes.bool,
  approvedStorageFileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  approvePrivilege: PropTypes.string,
  onApprove: PropTypes.func,
  onRevoke: PropTypes.func,
  approvePending: PropTypes.bool,
};

export default DisplayFiles;
